// SPDX-FileCopyrightText: 2016-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

import type {
  I18nRecord,
  SongbookBook,
  SongbookCollection,
  SongbookSourceBook,
  SongbookVariantTypeDefinition,
} from "./types";

/** Public URL prefix for all songbook data (PDFs, covers, icons, etc.). */
export const SONGBOOK_DATA_BASE_URL = "/songbook-data";

export function normalizeI18nRecordToMap(
  rec: I18nRecord | undefined | null,
  languages: readonly string[],
): Map<string, string> {
  const m = new Map<string, string>();
  if (!rec) return m;

  // Allow "*" as a wildcard (same value for all languages)
  const wildcard = rec["*"];
  if (typeof wildcard === "string") {
    for (const l of languages) m.set(l, wildcard);
  }

  for (const [k, v] of Object.entries(rec)) {
    if (k === "*") continue;
    if (v == null) continue;
    if (Array.isArray(v)) m.set(k, v.join(""));
    else m.set(k, String(v));
  }

  return m;
}

export function getEnabledBooks(collection: SongbookCollection): SongbookBook[] {
  return (collection.books ?? []).filter(b => !!b.enabled);
}

export function getBookViewId(book: SongbookBook, bookIndex1Based: number): string {
  const view = (book.view_id ?? "").trim();
  return view ? view : `book-${bookIndex1Based}`;
}

export function resolvePdfUrl(collection: SongbookCollection, book: SongbookBook): string {
  const baseDir = joinUrl(SONGBOOK_DATA_BASE_URL, collection.book_path);
  return joinUrl(baseDir, book.file);
}

/** Resolves the advertised range-index JSON without changing the canonical PDF URL. */
export function resolveMultikeyJsonUrl(
  collection: SongbookCollection,
  book: SongbookBook,
): string | null {
  if (typeof book.multikey_json_file !== "string" || !book.multikey_json_file.trim()) return null;
  const relative = (value: string): string | null => {
    if (value.length > 2048 || value.startsWith("/") || /[\\?#:\x00-\x1f]/.test(value)) return null;
    try {
      const segments = value.split("/").map(segment => decodeURIComponent(segment));
      if (
        segments.some(
          segment =>
            !segment || segment === "." || segment === ".." || /[\\/\x00-\x1f]/.test(segment),
        )
      )
        return null;
      return segments.map(encodeURIComponent).join("/");
    } catch {
      return null;
    }
  };
  const file = relative(book.multikey_json_file.trim());
  const configuredDirectory = collection.multikey_path ?? "multikey";
  if (typeof configuredDirectory !== "string") return null;
  const directory = configuredDirectory.trim();
  const path = directory ? relative(directory.replace(/\/+$/, "")) : "";
  if (!file || !file.toLowerCase().endsWith(".json") || path === null) return null;
  return joinUrl(joinUrl(SONGBOOK_DATA_BASE_URL, path), file);
}

export function resolveCoverUrl(collection: SongbookCollection, book: SongbookBook): string {
  const imgRelPath = collection.image_path ?? "";
  const imgBase = joinUrl(SONGBOOK_DATA_BASE_URL, imgRelPath);
  return joinUrl(imgBase, book.image);
}

export function resolveSocialImageUrl(collection: SongbookCollection, book: SongbookBook): string {
  const file = (book.social_image ?? "").trim();
  if (!file) return "";
  const socialRelPath = collection.social_path ?? "";
  const socialBase = joinUrl(SONGBOOK_DATA_BASE_URL, socialRelPath);
  return joinUrl(socialBase, file);
}

/**
 * Resolves the per-book JSON database URL for Search.
 *
 * Convention:
 * - The JSON lives under collection.json_path (relative to songbook-data root).
 * - The JSON file name matches the PDF file name, except the extension is `.json`.
 */
export function resolveBookJsonUrl(collection: SongbookCollection, book: SongbookBook): string {
  const jsonRelPath = (collection.json_path ?? "").trim();
  const jsonBase = joinUrl(SONGBOOK_DATA_BASE_URL, jsonRelPath);

  const pdfFile = String(book.file ?? "").trim();
  const base = pdfFile.replace(/\.[^/.]+$/, "");
  const jsonFile = base ? `${base}.json` : "";

  return jsonFile ? joinUrl(jsonBase, jsonFile) : "";
}

export function joinUrl(a: string, b: string): string {
  const a2 = (a ?? "").trim();
  const b2 = (b ?? "").trim();
  if (!a2) return b2;
  if (!b2) return a2;
  const left = a2.endsWith("/") ? a2.slice(0, -1) : a2;
  const right = b2.startsWith("/") ? b2.slice(1) : b2;
  return `${left}/${right}`;
}

export function normalizeSongbookCollection(raw: SongbookCollection): SongbookCollection {
  // If already in legacy shape (no book_sources), return as-is.
  const rawSources: SongbookSourceBook[] | undefined =
    (raw as any).book_sources ?? (raw as any).sources;
  if (!Array.isArray(rawSources) || rawSources.length === 0) {
    return raw;
  }

  const variantDefs: Record<string, SongbookVariantTypeDefinition> = {
    ...(raw as any).variant_types,
  };
  const books: SongbookBook[] = [];
  const usedVariantIds = new Set<string>();

  const defaultVariantIds = ["default"];

  const mergeTagIds = (
    sourceTags?: string[] | null,
    variantTags?: string[] | null,
    assetTags?: string[] | null,
  ): string[] | undefined => {
    const merged = new Set<string>();
    for (const arr of [sourceTags, variantTags, assetTags]) {
      if (!Array.isArray(arr)) continue;
      for (const id of arr) {
        const t = (id ?? "").trim();
        if (t) merged.add(t);
      }
    }
    return merged.size > 0 ? Array.from(merged) : undefined;
  };

  const variantsEnabledForSource = (src: SongbookSourceBook): string[] => {
    const enabled =
      src.variants_enabled?.filter(v => !!v && v.trim().length > 0).map(v => v.trim()) ?? [];
    return enabled.length > 0 ? enabled : defaultVariantIds;
  };

  const enabledSources = rawSources.filter(src => !!src?.enabled);

  for (const source of enabledSources) {
    const variantIds = variantsEnabledForSource(source);
    for (const variant of variantIds) {
      const asset = (source.variants ?? {})[variant];
      if (!asset) continue;

      usedVariantIds.add(variant);

      const def: SongbookVariantTypeDefinition | undefined = variantDefs[variant];

      const title_i18n = source.title_i18n;
      // Fallback order: per-asset override -> variant definition (global) -> source-level text -> null.
      const subtitle_i18n =
        asset.subtitle_i18n ?? def?.subtitle_i18n ?? source.subtitle_i18n ?? null;
      const description_i18n =
        asset.description_i18n ?? def?.description_i18n ?? source.description_i18n ?? null;
      const altview_url = asset.altview_url ?? source.altview_url ?? null;
      const image = asset.image ?? source.image ?? "";

      const tag_ids = mergeTagIds(source.tag_ids, def?.tag_ids ?? null, asset.tag_ids ?? null);

      const audio_links = asset.audio_links ?? source.audio_links ?? null;
      // Extra printouts are taken only from the variant asset (no source-level or variant-definition merging).
      const extra_printout_links = asset.extra_printout_links ?? undefined;

      const has_printout_booklet_file =
        asset.has_printout_booklet_file ?? source.has_printout_booklet_file ?? undefined;
      const has_printout_easy_file =
        asset.has_printout_easy_file ?? source.has_printout_easy_file ?? undefined;

      const variantViewPart = def?.view_id_part || variant;
      const view_id =
        asset.view_id ??
        (source.view_id_part
          ? `${source.view_id_part}-${variantViewPart}`
          : `${source.source_book_id}-${variantViewPart}`);

      books.push({
        enabled: !!source.enabled,
        source_book_id: source.source_book_id,
        variant,
        variant_label_i18n: def?.label_i18n ?? null,
        view_id,
        file: asset.file,
        ...(asset.multikey_json_file ? { multikey_json_file: asset.multikey_json_file } : {}),
        image,
        social_image: asset.social_image ?? null,
        title_i18n,
        subtitle_i18n: subtitle_i18n ?? undefined,
        description_i18n: description_i18n ?? undefined,
        altview_url,
        has_printout_booklet_file,
        has_printout_easy_file,
        extra_printout_links,
        audio_links: audio_links ?? undefined,
        tag_ids,
      });
    }
  }

  const filteredVariantDefs = Object.fromEntries(
    Object.entries(variantDefs).filter(([id]) => usedVariantIds.has(id)),
  );

  return {
    ...raw,
    variant_types: filteredVariantDefs,
    book_sources: enabledSources,
    books,
  };
}

export function pickLangFromAcceptLanguage(
  acceptLanguageHeader: string | undefined | null,
  allowed: readonly string[],
  fallback: string,
): string {
  if (!acceptLanguageHeader) return fallback;

  // Very small parser: pick the first language, then its 2-letter prefix.
  const first = acceptLanguageHeader.split(",")[0]?.trim();
  if (!first) return fallback;
  const full = first.split(";")[0].trim().replace("-", "_");
  if (allowed.includes(full)) return full;
  const short = full.slice(0, 2);
  if (allowed.includes(short)) return short;
  return fallback;
}

export function canonicalUrlForView(
  canonicalBaseUrl: string | null | undefined,
  view: string,
  lang?: string,
): string | null {
  if (!canonicalBaseUrl) return null;
  const u = new URL(canonicalBaseUrl);
  if (view && view !== "home") u.searchParams.set("view", view);
  if (lang) u.searchParams.set("lang", lang);
  return u.toString();
}
